import { useHead, useSeoMeta } from "@unhead/react"
import Navbar from "@/components/Navbar"
import CowCalfProfitCalculator from "@/components/CowCalfProfitCalculator"
import CowCalfProfitGuide from "@/components/CowCalfProfitGuide"
import AboutSection from "@/components/AboutSection"
import QuickLinksFooter from "@/components/QuickLinksFooter"

function CowCalfProfit() {
  useSeoMeta({
    title: 'Cow-Calf Profit Calculator | Free ROI & Break-Even',
    description: "Use our free cow calf profit calculator to estimate your ranch profitability. Factor in calf sales, cull cow revenue, feed costs, and all expenses to calculate net profit per cow and break-even price.",
    ogType: 'website',
    ogTitle: 'Cow-Calf Profit Calculator | Free ROI & Break-Even',
    ogDescription: "Estimate your cow-calf operation profitability. Calculate net profit per cow, ROI, and break-even price.",
    ogImage: 'https://www.cowcalculator.com/og-gestation.jpg',
    ogUrl: 'https://www.cowcalculator.com/cow-calf-profit-calculator',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Cow-Calf Profit Calculator | Free ROI & Break-Even',
    twitterDescription: "Estimate your cow-calf operation profitability. Calculate net profit per cow, ROI, and break-even price.",
    twitterImage: 'https://www.cowcalculator.com/og-gestation.jpg',
  })

  useHead({
    link: [{ rel: 'canonical', href: 'https://www.cowcalculator.com/cow-calf-profit-calculator' }],
  })

  return (
    <div className="min-h-screen bg-[#FEFAE0]">
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative bg-[#283618] overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: "radial-gradient(circle at 30% 40%, #D4A373 1px, transparent 1px), radial-gradient(circle at 70% 60%, #D4A373 1px, transparent 1px)",
              backgroundSize: "60px 60px, 80px 80px",
            }} />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-center">
            <div className="inline-flex items-center gap-2 bg-[#D4A373]/30 backdrop-blur-sm border border-[#D4A373]/40 rounded-full px-4 py-1.5 mb-5">
              <div className="w-2 h-2 rounded-full bg-[#D4A373] animate-pulse" />
              <span className="text-[#D4A373] text-xs sm:text-sm font-medium tracking-wide">
                Business &amp; Profit Tool
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FEFAE0] leading-tight">
              Cow Calf Profit{" "}
              <span className="text-[#D4A373]">Calculator</span>
            </h1>
            <p className="mt-4 sm:mt-5 text-base sm:text-lg text-[#B7E4C7]/90 max-w-2xl mx-auto leading-relaxed">
              Estimate your <strong>cow-calf operation profitability</strong> by factoring in calf sales revenue, cull cow income, and all major expense categories. Know your break-even price and profit per cow.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6">
              {[
                { value: "Free", label: "No Cost" },
                { value: "7", label: "Expense Categories" },
                { value: "3", label: "Operation Presets" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-[#D4A373]">{stat.value}</div>
                  <div className="text-xs text-[#B7E4C7]/60 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 48" fill="none" className="w-full">
              <path d="M0 48L60 40C120 32 240 16 360 12C480 8 600 16 720 20C840 24 960 24 1080 20C1200 16 1320 8 1380 4L1440 0V48H0Z" fill="#FEFAE0" />
            </svg>
          </div>
        </section>

        <CowCalfProfitCalculator />
        <CowCalfProfitGuide />
        <AboutSection />
      </main>
      <QuickLinksFooter />
    </div>
  )
}

export default CowCalfProfit
